import { Close } from "@mui/icons-material";
import {
  Alert,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { useGetCouponsForProductQuery } from "src/api/services/couponsApi";
import { PatientSubscriptions } from "src/api/types";
import { desktopSx } from "src/utils/styles/modalStyles";
import AddIcon from "@mui/icons-material/Add";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useAddPatientCouponMutation } from "src/api/services/patientCouponApi";

interface Props {
  patientId: number;
  onClose: () => void;
  open: boolean;
  subscription: PatientSubscriptions;
}

const AddCouponModal = ({ open, patientId, onClose, subscription }: Props) => {
  const { data } = useGetCouponsForProductQuery(
    subscription.product.slug ?? skipToken
  );
  const [addCoupon, { isLoading }] = useAddPatientCouponMutation();
  const [selectedCoupon, setSelectedCoupon] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const coupons = useMemo(() => {
    if (data == null) return [];

    return data;
  }, [data]);

  const handleAddCoupon = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (selectedCoupon == null) {
        setError("Please select a coupon");
        return;
      }

      const coupon = await addCoupon({
        coupon_id: selectedCoupon,
        patient_id: patientId,
        product_id: subscription.product_id,
      }).unwrap();

      if (coupon) {
        setMessage("Coupon successfully added to patient");
      }
    },
    [addCoupon, patientId, selectedCoupon, subscription.product_id]
  );

  const handleClose = useCallback(() => {
    setMessage("");
    setError("");
    setSelectedCoupon(null);
    onClose();
  }, [onClose]);

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Add coupon
          </Typography>
          <IconButton onClick={handleClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={2} component={"form"} onSubmit={handleAddCoupon}>
          <FormControl required>
            <TextField
              select
              value={selectedCoupon}
              label="Select coupon"
              onChange={(e) => setSelectedCoupon(Number(e.target.value))}
            >
              {coupons.map((coupon) => (
                <MenuItem
                  value={coupon.id}
                  key={coupon.id}
                >{`${coupon.name} ($${coupon.amount})`}</MenuItem>
              ))}
            </TextField>
          </FormControl>
          <Stack gap={1}>
            {error ? (
              <Typography sx={{ color: "red" }} textAlign="center">
                {error}
              </Typography>
            ) : null}
            {message ? <Alert severity="success">{message}</Alert> : null}
            <LoadingButton
              loading={isLoading}
              startIcon={<AddIcon />}
              disabled={!selectedCoupon}
              type="submit"
            >
              Add
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddCouponModal;
